import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Search, TrendingUp, TrendingDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface JobStatsExplorerProps {
  shopId: string;
}

interface JobStat {
  jobId: string;
  jobName: string;
  category: string;
  standardMinutes: number;
  avgActualMinutes: number;
  totalCompletions: number;
  efficiencyRatio: number;
}

export function JobStatsExplorer({ shopId }: JobStatsExplorerProps) {
  const [searchTerm, setSearchTerm] = useState("");

  const { data: jobStats, isLoading } = useQuery({
    queryKey: ['job-stats-direct', shopId],
    queryFn: async () => {
      // JBS-A1: read pre-aggregated rollups from `pensieve_job_stats`
      // instead of the pre-A1 unbounded 3-step chain (tickets ID-only →
      // ticket_jobs IN(...) → jobs IN(...) → client-side rollup). The
      // shop-wide aggregator (`supabase/functions/aggregate-job-stats`)
      // upserts one row per (shop_id, job_id) with `tech_id = NULL`
      // representing the all-techs aggregate; we scope to those rows and
      // join the FK-related `jobs` row inline for the UI's `name`,
      // `category`, and `standardMinutes` fields. Same canonical pattern
      // already in use by sibling `JobPerformanceTrends.tsx:39-48`
      // (FIX-R131-MED-2 anchor): `pensieve_job_stats + jobs!inner +
      // tech_id IS NULL + total_completions DESC + limit cap`.
      //
      // CSI-S1 preservation: the deleted-ticket exclusion that previously
      // ran client-side now lives in `aggregate-job-stats/index.ts`
      // (`.is('ticket.deleted_at', null)`), so the rollup respects the
      // soft-delete semantic eventually-consistently.
      //
      // IV-D-22 R72 FIX-1 ↔ R73 FIX-2: the aggregator writes
      // `efficiency_ratio` in book/actual convention (predicted/actual);
      // pre-A1 this component computed the same direction client-side.
      // Reading the column directly preserves the convention.
      const { data, error } = await supabase
        .from('pensieve_job_stats')
        .select(`
          job_id,
          total_completions,
          avg_actual_minutes,
          efficiency_ratio,
          jobs!inner(id, name, category, standard_minutes)
        `)
        .eq('shop_id', shopId)
        .is('tech_id', null)
        .order('total_completions', { ascending: false })
        .limit(20);

      if (error) throw error;
      if (!data?.length) return [];

      const stats: JobStat[] = data
        .filter((row: any) => row.jobs && (row.total_completions ?? 0) > 0)
        .map((row: any) => {
          const job = row.jobs;
          const avgActual = Number(row.avg_actual_minutes) || 0;
          // IV-D-22 R73 FIX-2: prefer the aggregator-stored
          // `efficiency_ratio` (book/actual). Fall back to deriving
          // `standard_minutes / avgActual` for legacy rows where the
          // aggregator hasn't yet populated the column. avgActual==0
          // collapses to ratio=1 to match the pre-A1 fallback.
          const storedEfficiency = row.efficiency_ratio == null
            ? null
            : Number(row.efficiency_ratio);
          const efficiency = storedEfficiency != null
            ? storedEfficiency
            : (avgActual > 0 ? (job.standard_minutes ?? 0) / avgActual : 1);

          return {
            jobId: row.job_id,
            jobName: job.name,
            category: job.category,
            standardMinutes: job.standard_minutes,
            avgActualMinutes: avgActual,
            totalCompletions: row.total_completions ?? 0,
            efficiencyRatio: efficiency,
          };
        });

      return stats;
    }
  });

  const filteredStats = jobStats?.filter(stat =>
    stat.jobName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stat.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return <Card className="p-4"><div className="h-64 animate-pulse bg-muted rounded" /></Card>;
  }

  return (
    <Card className="p-4">
      <h3 className="font-semibold text-foreground mb-4">Job Performance Stats</h3>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search jobs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {filteredStats?.map((stat) => {
          // IV-D-22 R73 FIX-2: with the corrected book/actual convention,
          // >=1.0 means the tech was at or faster than book (good).
          const isEfficient = stat.efficiencyRatio >= 1.0;

          return (
            <div key={stat.jobId} className="p-3 bg-muted/50 rounded-lg border border-border">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-medium text-sm text-foreground">{stat.jobName}</p>
                  <p className="text-xs text-muted-foreground">{stat.category}</p>
                </div>
                {isEfficient ? (
                  <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Efficient
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="gap-1">
                    <TrendingDown className="h-3 w-3" />
                    {(stat.efficiencyRatio * 100).toFixed(0)}%
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Avg Time</p>
                  <p className="font-mono font-medium text-foreground">
                    {stat.avgActualMinutes.toFixed(0)}m
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Book Time</p>
                  <p className="font-mono font-medium text-foreground">
                    {stat.standardMinutes}m
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Count</p>
                  <p className="font-mono font-medium text-foreground">
                    {stat.totalCompletions}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        {filteredStats?.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            {searchTerm ? `No jobs found matching "${searchTerm}"` : "No completed jobs found"}
          </p>
        )}
      </div>
    </Card>
  );
}
